import { pageTitle } from '@enp/brand';
import { Button, cn, EmptyState, PageHeader } from '@enp/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import type { NavItem } from './nav.js';

/** Sets the browser tab title: "<page> · <product> Admin". */
export function useDocumentTitle(page?: string): void {
  useEffect(() => {
    document.title = pageTitle(page, 'admin');
  }, [page]);
}

/**
 * A header's meta line, "Supplier · Main bakery · expected 2 Oct" (UI8C-12): each item wraps
 * whole, and its dot ends a line, never starts one, so a narrow screen shows no orphan
 * "· 30 Sep, 09:00" on the second line. Empty items are left out.
 */
export function MetaList({ items, className }: { items: ReactNode[]; className?: string }) {
  const shown = items.filter((i) => i !== null && i !== undefined && i !== false && i !== '');
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-y-1', className)}>
      {shown.map((item, i) => (
        <span key={i} className="inline-flex items-center whitespace-nowrap">
          {item}
          {i < shown.length - 1 && <span className="px-1.5">·</span>}
        </span>
      ))}
    </span>
  );
}

/**
 * While a sticky save bar sits at the bottom of the page, the page scrolls a focused field
 * clear of it (WCAG 2.4.11, focus not obscured; B-030): the bar's height is kept as scroll
 * padding at the bottom of the document. Render it inside the bar, so it lasts as long as the bar.
 */
export function RoomForStickyBar(): null {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('scroll-pb-32');
    return () => root.classList.remove('scroll-pb-32');
  }, []);
  return null;
}

export interface ComingSoonProps {
  title: string;
  description: string;
  icon: LucideIcon;
  /** What will live here, in one or two plain sentences. */
  whatsComing: ReactNode;
  /** Where to go instead: the nearest part of the product that works today. */
  next?: { to: NavItem['to']; label: string };
}

/**
 * A section that is in the navigation but not switched on yet. Plain words and one way
 * onward; no disabled stand-ins for future actions (they are dead tab stops, UI0A-206).
 * Compact, so the way onward is above the fold on a tablet.
 */
export function ComingSoon({
  title,
  description,
  icon,
  whatsComing,
  next = { to: '/', label: 'Go to the dashboard' },
}: ComingSoonProps) {
  useDocumentTitle(title);
  return (
    <>
      <PageHeader title={title} description={description} />
      <EmptyState
        compact
        icon={icon}
        title={`${title} isn’t switched on yet`}
        description={whatsComing}
        action={
          <Button variant="secondary" asChild>
            <Link to={next.to}>
              {next.label}
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        }
      />
    </>
  );
}
